import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Space, Card, Typography, Tooltip } from 'antd';
import { DeleteOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { createStyles } from 'antd-style';
import { FormField } from '../../SmartDataTable/types';
import { FormFieldRenderer } from '../forms/CommonForm/FormFieldRenderer';
import { formatNumber } from '@src/utils/numberUtils';

const { Text } = Typography;

// Row action types
export type RowAction = 'create' | 'update' | 'delete' | '';

// Row state interface
export interface TableRow {
  id: string | number;
  [key: string]: any;
  _action?: RowAction; // Track the action for this row ('' = clean, no action needed)
  _originalData?: any; // Store original data for comparison
}

// Action callback types
export interface TableActionCallbacks {
  onCreate?: (row: TableRow, allRows: TableRow[]) => Promise<void> | void;
  onUpdate?: (row: TableRow, allRows: TableRow[]) => Promise<void> | void;
  onDelete?: (row: TableRow, allRows: TableRow[]) => Promise<void> | void;
  onActionComplete?: (
    action: RowAction,
    row: TableRow,
    allRows: TableRow[]
  ) => void;
  onActionError?: (action: RowAction, row: TableRow, error: any) => void;
}

// Utility function to format values based on format type
const formatValue = (
  value: any,
  format?: string,
  decimalPlaces: number = 2
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    return String(value);
  }

  switch (format) {
    case 'currency':
      return `₹${numValue.toFixed(decimalPlaces)}`;
    case 'percentage':
      return `${numValue.toFixed(decimalPlaces)}%`;
    case 'decimal':
    case 'number':
      return numValue.toFixed(decimalPlaces);
    default:
      return String(value);
  }
};

// Validation utility function for table cells
const validateTableCell = (
  rowIndex: number,
  columnDataIndex: string,
  columns: any[],
  dataSource: any[]
): { isValid: boolean; error?: string } => {
  const column = columns.find(col => col.dataIndex === columnDataIndex);
  if (!column || (!column.required && !column.field?.required)) {
    return { isValid: true };
  }

  const row = dataSource[rowIndex];
  if (!row) return { isValid: true };

  const cellValue = row[columnDataIndex];

  // Check if the cell is empty
  if (cellValue === null || cellValue === undefined || cellValue === '') {
    // Check if there's a default value that should be used
    if (column.field?.defaultValue !== undefined) {
      return { isValid: true }; // Has default value, so it's valid
    }

    const columnTitle = column?.title || columnDataIndex;
    return { isValid: false, error: `${columnTitle} is required` };
  }

  // For resourceSelect fields, check if value property exists
  if (typeof cellValue === 'object' && cellValue.hasOwnProperty('value')) {
    if (
      cellValue.value === null ||
      cellValue.value === undefined ||
      cellValue.value === ''
    ) {
      const columnTitle = column?.title || columnDataIndex;
      return { isValid: false, error: `${columnTitle} is required` };
    }
  }

  // For resourceSelect fields that might be stored as simple IDs, check if it's truthy
  if (column.field?.type === 'resourceSelect') {
    if (!cellValue) {
      const columnTitle = column?.title || columnDataIndex;
      return { isValid: false, error: `${columnTitle} is required` };
    }
  }

  // Additional validation for specific field types
  if (column.field?.validation) {
    const validation = column.field.validation;

    // Pattern validation
    if (validation.pattern && typeof cellValue === 'string') {
      const regex = new RegExp(validation.pattern);
      if (!regex.test(cellValue)) {
        return {
          isValid: false,
          error:
            validation.message ||
            `${column?.title || columnDataIndex} format is invalid`,
        };
      }
    }

    // Min validation for numbers
    if (validation.min !== undefined && typeof cellValue === 'number') {
      if (cellValue < validation.min) {
        return {
          isValid: false,
          error: `${column?.title || columnDataIndex} must be at least ${validation.min}`,
        };
      }
    }

    // Max validation for numbers
    if (validation.max !== undefined && typeof cellValue === 'number') {
      if (cellValue > validation.max) {
        return {
          isValid: false,
          error: `${column?.title || columnDataIndex} must be at most ${validation.max}`,
        };
      }
    }
  }

  return { isValid: true };
};

// Enhanced validation for entire table
const validateTableRows = (
  dataSource: any[],
  columns: any[],
  minRows: number = 0
): {
  isValid: boolean;
  errors: string[];
  rowErrors: { [rowIndex: number]: string[] };
} => {
  const errors: string[] = [];
  const rowErrors: { [rowIndex: number]: string[] } = {};

  // Check minimum rows requirement
  if (dataSource.length < minRows) {
    errors.push(`Table must have at least ${minRows} row(s)`);
  }

  // Validate each row
  dataSource.forEach((row, rowIndex) => {
    const rowErrorMessages: string[] = [];

    columns.forEach(column => {
      if (column.required || column.field?.required) {
        const result = validateTableCell(
          rowIndex,
          column.dataIndex,
          columns,
          dataSource
        );
        if (!result.isValid && result.error) {
          rowErrorMessages.push(result.error);
        }
      }
    });

    if (rowErrorMessages.length > 0) {
      rowErrors[rowIndex] = rowErrorMessages;
      errors.push(`Row ${rowIndex + 1}: ${rowErrorMessages.join(', ')}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    rowErrors,
  };
};

const useStyles = createStyles(({ token }) => ({
  tableFieldContainer: {
    position: 'relative',
  },
  card: {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadius,
  },
  cardHead: {
    padding: '12px 16px',
  },
  cardBody: {
    padding: 0,
  },
  table: {
    border: 'none',
    '& .ant-table-thead > tr > th, & .ant-table-tbody > tr > td': {
      wordBreak: 'break-word',
      whiteSpace: 'normal',
    },
    '& .ant-table-cell': {
      textAlign: 'inherit',
    },
    '& .ant-table-container': {
      overflowX: 'auto',
    },
    '& .ant-table-content': {
      overflowX: 'auto',
    },
    '& .ant-table': {
      minWidth: 'max-content',
    },
  },
  addButtonContainer: {
    padding: '20px 16px',
    borderTop: `2px solid ${token.colorBorder}`,
    marginTop: 0,
  },
  summaryRow: {
    backgroundColor: token.colorBgContainer,
  },
  summaryText: {
    color: token.colorPrimary,
    fontSize: '14px',
  },
  errorCell: {
    border: `1px solid ${token.colorError} !important`,
    borderRadius: token.borderRadius,
    padding: '2px',
    backgroundColor: `${token.colorErrorBg} !important`,
  },
  errorMessage: {
    color: token.colorError,
    fontSize: '9px',
    marginTop: '2px',
    marginLeft: '4px',
    lineHeight: '1.2',
    fontWeight: '500',
  },
  rowNew: {
    backgroundColor: '#cbedba', // Darker green background
    border: `1px solid ${token.colorSuccess}`,
  },
  rowModified: {
    backgroundColor: '#fbe4b6', // Darker orange background
    border: `1px solid ${token.colorWarning}`,
  },
  rowDeleted: {
    backgroundColor: '#f98787', // Darker red background
    border: `1px solid ${token.colorError}`,
    opacity: 0.8, // Increased opacity for better visibility
  },
  validationSummary: {
    padding: '12px 16px',
    borderTop: `1px solid ${token.colorBorder}`,
    backgroundColor: token.colorErrorBg,
  },
  validationTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  validationErrors: {
    margin: 0,
    paddingLeft: '16px',
  },
  validationError: {
    fontSize: '12px',
    color: token.colorError,
    marginBottom: '4px',
  },
}));

/**
 * TableField Component
 *
 * A form field component that renders a dynamic table with editable cells.
 * Supports validation for required fields at both table and cell levels.
 *
 * Validation Features:
 * - Table-level validation: Validates minimum rows and overall table requirements
 * - Cell-level validation: Shows individual cell errors for required fields
 * - Visual indicators: Red borders and error messages for invalid cells
 * - Real-time validation: Triggers validation on cell value changes
 *
 * Action Management Features:
 * - Track row actions (create/update/delete)
 * - Callback support for backend operations
 * - Visual indicators for row states
 * - Action completion handling
 *
 * Usage Example:
 * ```typescript
 * {
 *   name: 'items',
 *   label: 'Items',
 *   type: 'table',
 *   required: true,
 *   tableProps: {
 *     minRows: 1,
 *     columns: [
 *       {
 *         key: 'name',
 *         title: 'Name',
 *         dataIndex: 'name',
 *         required: true,
 *         field: {
 *           type: 'text',
 *           placeholder: 'Enter name'
 *         }
 *       },
 *       {
 *         key: 'quantity',
 *         title: 'Quantity',
 *         dataIndex: 'quantity',
 *         required: true,
 *         field: {
 *           type: 'number',
 *           placeholder: 'Enter quantity'
 *         }
 *       }
 *     ],
 *     actionCallbacks: {
 *       onCreate: async (row, allRows) => {
 *         // Handle row creation on backend
 *         await api.createRow(row);
 *       },
 *       onUpdate: async (row, allRows) => {
 *         // Handle row update on backend
 *         await api.updateRow(row);
 *       },
 *       onDelete: async (row, allRows) => {
 *         // Handle row deletion on backend
 *         await api.deleteRow(row.id);
 *       }
 *     }
 *   }
 * }
 * ```
 */
export interface TableColumn {
  key: string;
  title: string;
  dataIndex: string;
  field?: FormField; // Form field configuration for this column
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  fixed?: 'left' | 'right';
  align?: 'left' | 'center' | 'right';
  format?: 'number' | 'currency' | 'percentage' | 'decimal';
  decimalPlaces?: number;
  render?: (
    value: any,
    record: any,
    index: number,
    form: any
  ) => React.ReactNode;
  editable?: boolean;
  required?: boolean;
  calculateValue?: (rowData: any, allRows: any[], form: any) => any; // For calculated fields
  onValueChange?: (value: any, rowData: any, allRows: any[], form: any) => void; // For value change callbacks
}

export interface TableFieldProps {
  value?: TableRow[];
  onChange?: (value: TableRow[]) => void;
  columns: TableColumn[];
  title?: string;
  addButtonText?: string;
  emptyText?: string;
  maxRows?: number;
  minRows?: number;
  showRowNumbers?: boolean;
  showAddButton?: boolean; // Whether to show add button
  addButtonPosition?: 'header' | 'footer'; // Position of add button
  showActions?: boolean; // Whether to show default actions column
  customActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (record: any, index: number, form: any) => void;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    tooltip?: string;
    confirmMessage?: string;
  }>;
  summaryRow?: {
    enabled: boolean;
    fields: string[];
    calculateTotal?: (field: string, allRows: any[]) => any;
  };
  form?: any; // Ant Design form instance
  fieldName?: string; // Field name for form validation
  onSummaryChange?: (summary: any) => void; // Callback when summary changes
  actionCallbacks?: TableActionCallbacks; // Callbacks for row actions
  showRowStates?: boolean; // Whether to show visual indicators for row states
  onRowDelete?: (
    deletedRow: TableRow,
    remainingRows: TableRow[],
    form: any
  ) => void; // Callback when a row is deleted
  style?: React.CSSProperties;
  className?: string;
}

export const TableField: React.FC<TableFieldProps> = ({
  value = [],
  onChange,
  columns,
  title,
  addButtonText = 'Add Row',
  emptyText = 'No data',
  maxRows,
  minRows = 1,
  showRowNumbers = true,
  showAddButton = true,
  addButtonPosition = 'header',
  showActions = true,
  customActions = [],
  summaryRow,
  form,
  fieldName,
  onSummaryChange,
  actionCallbacks,
  showRowStates = true,
  onRowDelete,
  style,
  className,
}) => {
  const { styles } = useStyles();

  // Helper function to get default value based on field type
  const getDefaultValueByType = (field?: FormField): any => {
    // If field has a specific defaultValue, use it
    if (field?.defaultValue !== undefined) {
      return field.defaultValue;
    }

    // Otherwise, use type-specific defaults
    switch (field?.type) {
      case 'text':
      case 'textarea':
      case 'email':
      case 'password':
      case 'telephone':
      case 'markdown':
      case 'richtext':
        return '';
      case 'number':
        // For number fields, return proper decimal number if specified
        if (field.format === 'decimal' && field.decimalPlaces !== undefined) {
          return formatNumber(0, field.decimalPlaces);
        }
        return 0;
      case 'select':
      case 'resourceSelect':
      case 'multiselect':
        return null; // Changed from 0 to null to avoid default ID selection
      case 'date':
      case 'datetime':
        return null;
      case 'checkbox':
      case 'switch':
        return false;
      case 'radio':
        return null;
      case 'file':
        return null;
      case 'table':
        return [];
      default:
        return '';
    }
  };

  // Helper function to create a new row with default values
  const createNewRow = (id: string | number): TableRow => {
    const newRow: TableRow = {
      id,
      _action: 'create' as RowAction,
    };

    // Add default values for all columns (but don't render them in form initially)
    columns.forEach(column => {
      if (column.field) {
        // Only set default values for non-select fields to avoid unwanted selections
        if (
          column.field.type !== 'select' &&
          column.field.type !== 'resourceSelect' &&
          column.field.type !== 'multiselect'
        ) {
          newRow[column.dataIndex] = getDefaultValueByType(column.field);
        }
      }
    });

    return newRow;
  };

  // Initialize with minimum required rows if value is empty
  const initialValue =
    value.length === 0 && minRows > 0
      ? Array.from({ length: minRows }, (_, index) =>
          createNewRow(`initial-row-${index}`)
        )
      : value.map(row => ({
          ...row,
          _action: (row._action || '') as RowAction, // Default to clean state if no action specified
          _originalData: row._originalData || { ...row },
        }));

  const [dataSource, setDataSource] = useState<TableRow[]>(initialValue);
  const [summary, setSummary] = useState<any>({});
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());
  const [validationState, setValidationState] = useState<{
    isValid: boolean;
    errors: string[];
    rowErrors: { [rowIndex: number]: string[] };
  }>({ isValid: true, errors: [], rowErrors: {} });

  // Ref to track previous validation state to prevent unnecessary updates
  const prevValidationRef = useRef<string>('');

  // Update dataSource when value prop changes
  useEffect(() => {
    const newDataSource =
      value.length === 0 && minRows > 0
        ? Array.from({ length: minRows }, (_, index) =>
            createNewRow(`initial-row-${index}`)
          )
        : value.map(row => ({
            ...row,
            _action: (row._action || '') as RowAction, // Default to clean state if no action specified
            _originalData: row._originalData || { ...row },
          }));

    setDataSource(newDataSource);

    // If we created initial rows, update the form value
    if (value.length === 0 && minRows > 0 && onChange) {
      onChange(newDataSource);
    }
  }, [value, minRows, onChange, columns]);

  // Ensure validation is properly set up when form changes
  useEffect(() => {
    if (form && fieldName) {
      // Set up validation for the table field
      form.validateFields([fieldName]).catch(() => {
        // This is expected when there are validation errors
      });
    }
  }, [form, fieldName]);

  // Calculate summary when data changes
  useEffect(() => {
    if (summaryRow?.enabled && summaryRow.fields) {
      const newSummary: any = {};
      // Filter out deleted rows for calculation purposes
      const activeRows = dataSource.filter(row => row._action !== 'delete');

      summaryRow.fields.forEach(field => {
        if (summaryRow.calculateTotal) {
          newSummary[field] = summaryRow.calculateTotal(field, activeRows);
        } else {
          // Default calculation: sum for numbers, count for others
          const values = activeRows
            .map(row => row[field])
            .filter(v => v !== undefined && v !== null);

          if (values.length > 0) {
            // Check if the first value is a number or a string that can be converted to a number
            const firstValue = values[0];
            const isNumeric =
              typeof firstValue === 'number' ||
              (typeof firstValue === 'string' && !isNaN(Number(firstValue)));

            if (isNumeric) {
              newSummary[field] = values.reduce(
                (sum, val) => sum + (Number(val) || 0),
                0
              );
            } else {
              newSummary[field] = values.length;
            }
          } else {
            newSummary[field] = 0;
          }
        }
      });

      setSummary(newSummary);
      onSummaryChange?.(newSummary);
    }
  }, [dataSource, summaryRow, onSummaryChange]);

  // Handle row addition
  const handleAddRow = useCallback(() => {
    if (maxRows && dataSource.length >= maxRows) return;

    const newRow = createNewRow(Date.now() + Math.random());
    const newDataSource = [...dataSource, newRow];
    setDataSource(newDataSource);
    onChange?.(newDataSource);
  }, [dataSource, maxRows, onChange, columns]);

  // Handle row deletion
  const handleDeleteRow = useCallback(
    (index: number) => {
      if (dataSource.length <= minRows) return;

      const row = dataSource[index];
      const newDataSource = [...dataSource];

      if (row._action === 'create') {
        // If it's a new row, remove it completely
        newDataSource.splice(index, 1);
      } else {
        // If it's an existing row (clean or update), mark it for deletion
        newDataSource[index] = {
          ...row,
          _action: 'delete',
        };
      }

      setDataSource(newDataSource);
      onChange?.(newDataSource);

      // Trigger onRowDelete callback if provided
      if (onRowDelete) {
        onRowDelete(row, newDataSource, form);
      }
    },
    [dataSource, minRows, onChange, onRowDelete, form]
  );

  // Handle cell value change
  const handleCellChange = useCallback(
    (index: number, key: string, value: any) => {
      const newDataSource = [...dataSource];
      const row = newDataSource[index];

      // Find the column to determine the field type
      const targetColumn = columns.find(col => col.dataIndex === key);

      // Check if the value is actually different to prevent infinite loops
      const currentValue = row[key];
      if (currentValue === value) {
        return; // No change needed
      }

      // Transform value based on field type
      let transformedValue = value;
      if (
        targetColumn?.field?.type === 'number' &&
        value !== null &&
        value !== undefined &&
        value !== ''
      ) {
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          // For decimal fields, store the actual decimal number
          if (
            targetColumn.field.format === 'decimal' &&
            targetColumn.field.decimalPlaces !== undefined
          ) {
            transformedValue = formatNumber(
              numValue,
              targetColumn.field.decimalPlaces
            );
          } else {
            transformedValue = numValue;
          }
        }
      } else if (
        (targetColumn?.field?.type === 'checkbox' ||
          targetColumn?.field?.type === 'switch') &&
        value !== null &&
        value !== undefined
      ) {
        transformedValue = Boolean(value);
      }

      // Check if transformed value is different from current value
      if (currentValue === transformedValue) {
        return; // No change needed after transformation
      }

      // Update the row data
      newDataSource[index] = {
        ...row,
        [key]: transformedValue,
        _action: row._action === 'create' ? 'create' : 'update', // Clean rows become 'update' when modified
      };

      // Handle calculated fields (but preserve focus by not re-rendering the entire row)
      const calculatedFields: { [key: string]: any } = {};
      columns.forEach(column => {
        if (column.calculateValue && column.dataIndex !== key) {
          // Don't recalculate the field that's being changed
          const calculatedValue = column.calculateValue(
            newDataSource[index],
            newDataSource,
            form
          );
          // Only update if the calculated value is different from current value
          if (calculatedValue !== row[column.dataIndex]) {
            calculatedFields[column.dataIndex] = calculatedValue;
          }
        }
      });

      // Update calculated fields without triggering a full re-render
      if (Object.keys(calculatedFields).length > 0) {
        newDataSource[index] = { ...newDataSource[index], ...calculatedFields };
      }

      // Update the data source first
      setDataSource(newDataSource);
      onChange?.(newDataSource);

      // Handle value change callbacks AFTER updating the data source to prevent infinite loops
      // Use setTimeout to ensure the state update has completed
      setTimeout(() => {
        columns.forEach(column => {
          if (column.onValueChange && column.dataIndex === key) {
            column.onValueChange(
              transformedValue,
              newDataSource[index],
              newDataSource,
              form
            );
          }
        });
      }, 0);
    },
    [dataSource, columns, form, onChange, fieldName]
  );

  // Execute action callbacks
  const executeAction = useCallback(
    async (action: RowAction, row: TableRow) => {
      if (!actionCallbacks) return;

      const actionKey = `${row.id}-${action}`;
      setPendingActions(prev => new Set(prev).add(actionKey));

      try {
        switch (action) {
          case 'create':
            if (actionCallbacks.onCreate) {
              await actionCallbacks.onCreate(row, dataSource);
            }
            break;
          case 'update':
            if (actionCallbacks.onUpdate) {
              await actionCallbacks.onUpdate(row, dataSource);
            }
            break;
          case 'delete':
            if (actionCallbacks.onDelete) {
              await actionCallbacks.onDelete(row, dataSource);
            }
            break;
        }

        // Mark action as completed
        actionCallbacks.onActionComplete?.(action, row, dataSource);

        // Update row state after successful action
        const newDataSource = [...dataSource];
        const rowIndex = newDataSource.findIndex(r => r.id === row.id);
        if (rowIndex !== -1) {
          newDataSource[rowIndex] = {
            ...newDataSource[rowIndex],
            _action: '', // Set back to clean state after successful operation
            _originalData: { ...newDataSource[rowIndex] },
          };
          setDataSource(newDataSource);
          onChange?.(newDataSource);
        }
      } catch (error) {
        console.error(`Error executing ${action} action:`, error);
        actionCallbacks.onActionError?.(action, row, error);
      } finally {
        setPendingActions(prev => {
          const newSet = new Set(prev);
          newSet.delete(actionKey);
          return newSet;
        });
      }
    },
    [actionCallbacks, dataSource, onChange]
  );

  // Get row class based on state
  const getRowClassName = useCallback(
    (record: TableRow, index: number) => {
      let className = '';

      if (showRowStates) {
        if (record._action === 'delete') className += ` ${styles.rowDeleted}`;
        else if (record._action === 'create') className += ` ${styles.rowNew}`;
        else if (record._action === 'update')
          className += ` ${styles.rowModified}`;
      }

      // Remove row-level error styling - we'll handle errors at cell level only
      // This prevents the entire row from getting red borders

      return className.trim();
    },
    [showRowStates, styles]
  );

  // Get validation error for a specific cell
  const getCellValidationError = useCallback(
    (rowIndex: number, columnDataIndex: string) => {
      // Only check for errors if this specific cell is required
      const column = columns.find(col => col.dataIndex === columnDataIndex);
      const isRequired = column?.required || column?.field?.required;

      if (!isRequired) {
        return undefined;
      }

      // Check validation state for row-specific errors first
      const rowErrors = validationState.rowErrors[rowIndex];
      if (rowErrors && rowErrors.length > 0) {
        // Find the specific error for this column
        const columnError = rowErrors.find(error => {
          const columnTitle = column?.title || columnDataIndex;
          return error.includes(columnTitle) || error.includes(columnDataIndex);
        });
        if (columnError) {
          return columnError;
        }
      }

      // If no row-specific error found, check if this specific cell is invalid
      const result = validateTableCell(
        rowIndex,
        columnDataIndex,
        columns,
        dataSource
      );
      if (!result.isValid) {
        return result.error;
      }

      return undefined;
    },
    [validationState.rowErrors, columns, dataSource]
  );

  // Effect to validate table data whenever it changes
  useEffect(() => {
    const validation = validateTableRows(dataSource, columns, minRows);
    const validationString = JSON.stringify(validation);

    // Only update validation state if it has actually changed
    if (prevValidationRef.current !== validationString) {
      setValidationState(validation);
      prevValidationRef.current = validationString;

      // Update form validation state only if validation status changed
      if (form && fieldName) {
        const currentErrors = form.getFieldError(fieldName);
        const currentErrorsString = JSON.stringify(currentErrors);
        const newErrorsString = JSON.stringify(validation.errors);

        if (currentErrorsString !== newErrorsString) {
          form.setFields([
            {
              name: fieldName,
              value: dataSource,
              errors: validation.errors,
            },
          ]);
        }
      }
    }
  }, [dataSource, columns, minRows, form, fieldName]);

  // Render form field for a cell with proper validation
  const renderFormField = (column: TableColumn, record: any, index: number) => {
    if (!column.field) {
      return record[column.dataIndex] || '';
    }

    const fieldValue = record[column.dataIndex];
    const cellError = getCellValidationError(index, column.dataIndex);
    const isRequired = column.required || column.field?.required;

    // Create a form context for the field that integrates with the parent form
    const fieldFormContext = {
      getFieldValue: () => {
        // For select/resourceSelect fields, don't return default values to avoid unwanted selections
        if (
          column.field?.type === 'select' ||
          column.field?.type === 'resourceSelect' ||
          column.field?.type === 'multiselect'
        ) {
          return fieldValue !== null && fieldValue !== undefined
            ? fieldValue
            : null;
        }

        // Return defaultValue if the field value is empty/undefined and defaultValue exists
        if (
          (fieldValue === null ||
            fieldValue === undefined ||
            fieldValue === '') &&
          column.field?.defaultValue !== undefined
        ) {
          return column.field.defaultValue;
        }
        // If no value and no specific defaultValue, use type-specific default
        if (
          fieldValue === null ||
          fieldValue === undefined ||
          fieldValue === ''
        ) {
          return getDefaultValueByType(column.field);
        }
        return fieldValue;
      },
      setFields: (fields: any[]) => {
        const value = fields[0]?.value;
        handleCellChange(index, column.dataIndex, value);

        // Don't trigger validation here - let the handleCellChange handle it
      },
      getFieldError: () => {
        // Only return errors for this specific cell
        return cellError ? [cellError] : [];
      },
      validateFields: (names: string[]) => {
        // Don't validate the entire form from individual cells
        return Promise.resolve();
      },
      // Add a method to force validation check
      forceValidate: () => {
        // Don't force validation from individual cells
      },
    };

    return (
      <div
        className={`${styles.tableFieldContainer} ${cellError && cellError.trim() !== '' ? styles.errorCell : ''}`}
        style={{ textAlign: column.align || 'left' }}
      >
        <FormFieldRenderer
          key={`${column.dataIndex}_${index}_${record.id}`}
          field={{
            ...column.field,
            name: `${column.dataIndex}_${index}`,
            required: isRequired,
            align: column.field?.align || column.align,
            format: column.field?.format || column.format,
            decimalPlaces: column.field?.decimalPlaces || column.decimalPlaces,
          }}
          form={fieldFormContext}
        />
        {cellError && <div className={styles.errorMessage}>{cellError}</div>}
      </div>
    );
  };

  // Table columns configuration
  const tableColumns = [
    ...(showRowNumbers
      ? [
          {
            title: '#',
            dataIndex: 'rowNumber',
            key: 'rowNumber',
            width: 50,
            fixed: 'left' as const,
            render: (_: any, __: any, index: number) => index + 1,
          },
        ]
      : []),
    ...columns.map(column => {
      const antdColumn: any = { ...column };

      // Initialize style object
      antdColumn.style = {
        ...antdColumn.style,
      };

      // Handle minWidth and maxWidth
      if (column.minWidth) {
        antdColumn.style.minWidth = column.minWidth;
      }
      if (column.maxWidth) {
        antdColumn.style.maxWidth = column.maxWidth;
      }

      // Handle alignment with CSS text-align
      if (column.align) {
        antdColumn.align = column.align;
        antdColumn.style.textAlign = column.align;
      }

      return {
        ...antdColumn,
        render: (value: any, record: any, index: number) => {
          if (column.render) {
            return column.render(value, record, index, form);
          }
          if (column.field) {
            return renderFormField(column, record, index);
          }

          // Apply formatting if specified
          if (column.format) {
            const formattedValue = formatValue(
              value,
              column.format,
              column.decimalPlaces
            );
            return (
              <div style={{ textAlign: column.align || 'left' }}>
                {formattedValue}
              </div>
            );
          }

          return (
            <div style={{ textAlign: column.align || 'left' }}>
              {value || ''}
            </div>
          );
        },
      };
    }),
    ...(showActions
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right' as const,
            render: (_: any, record: TableRow, index: number) => {
              const actionKey = `${record.id}-${record._action}`;
              const isPending = pendingActions.has(actionKey);

              return (
                <Space>
                  {/* Custom Actions */}
                  {customActions.map(action => (
                    <Tooltip
                      key={action.key}
                      title={action.tooltip || action.label}
                    >
                      <Button
                        type={action.type || 'text'}
                        size="small"
                        icon={action.icon}
                        onClick={() => action.onClick(record, index, form)}
                        danger={action.danger}
                        style={{
                          color: action.danger ? '#ff4d4f' : '#1890ff',
                          border: 'none',
                          padding: '0 4px',
                        }}
                      />
                    </Tooltip>
                  ))}

                  {/* Action Callbacks (Save/Delete for row states) */}
                  {actionCallbacks &&
                    record._action !== '' &&
                    record._action !== 'update' && (
                      <Button
                        type="primary"
                        size="small"
                        loading={isPending}
                        onClick={() => executeAction(record._action!, record)}
                        disabled={record._action === 'delete'}
                      >
                        {record._action === 'create' ? 'Save' : 'Delete'}
                      </Button>
                    )}

                  {/* Default Delete Button */}
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteRow(index)}
                    disabled={
                      dataSource.length <= minRows ||
                      record._action === 'delete'
                    }
                    danger
                  />
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  // Render add button based on position
  const renderAddButton = () => {
    if (!showAddButton) return null;

    return (
      <Button
        type="dashed"
        icon={<ShoppingCartOutlined />}
        onClick={handleAddRow}
        disabled={maxRows ? dataSource.length >= maxRows : false}
        size="middle"
      >
        {addButtonText}
      </Button>
    );
  };

  return (
    <div>
      <Card
        size="small"
        className={`${styles.card} ${className || ''}`}
        style={style}
        extra={addButtonPosition === 'header' ? renderAddButton() : undefined}
        styles={{
          header: {
            padding: '12px 16px',
          },
          body: {
            padding: '0',
          },
        }}
      >
        <Table
          dataSource={dataSource}
          columns={tableColumns}
          pagination={false}
          size="small"
          scroll={{
            x: 'max-content',
          }}
          locale={{ emptyText }}
          className={styles.table}
          tableLayout="fixed"
          rowClassName={getRowClassName}
          rowKey="id"
          summary={
            summaryRow?.enabled
              ? pageData => {
                  if (pageData.length === 0) return null;

                  return (
                    <Table.Summary.Row
                      className={styles.summaryRow}
                      key="summary-row"
                    >
                      {showRowNumbers && (
                        <Table.Summary.Cell index={0} key="summary-total-label">
                          <Text strong className={styles.summaryText}>
                            Total
                          </Text>
                        </Table.Summary.Cell>
                      )}
                      {columns.map((column, index) => {
                        const isSummaryField = summaryRow.fields.includes(
                          column.dataIndex
                        );
                        const summaryValue = summary[column.dataIndex];

                        return (
                          <Table.Summary.Cell
                            key={`summary-cell-${column.key}`}
                            index={showRowNumbers ? index + 1 : index}
                            align={column.align}
                          >
                            {isSummaryField ? (
                              <div
                                style={{ textAlign: column.align || 'left' }}
                              >
                                <Text strong className={styles.summaryText}>
                                  {column.format
                                    ? formatValue(
                                        summaryValue,
                                        column.format,
                                        column.decimalPlaces
                                      )
                                    : typeof summaryValue === 'number'
                                      ? `${summaryValue.toFixed(2)}`
                                      : summaryValue}
                                </Text>
                              </div>
                            ) : null}
                          </Table.Summary.Cell>
                        );
                      })}
                      <Table.Summary.Cell
                        key="summary-actions-cell"
                        index={
                          showRowNumbers ? columns.length + 1 : columns.length
                        }
                      />
                    </Table.Summary.Row>
                  );
                }
              : undefined
          }
        />
        {addButtonPosition === 'footer' && (
          <div className={styles.addButtonContainer}>{renderAddButton()}</div>
        )}

        {/* Validation Summary */}
        {/* {!validationState.isValid && validationState.errors.length > 0 && (
           <div className={styles.validationSummary}>
             <Text type="danger" className={styles.validationTitle}>
               Validation Errors:
             </Text>
             <ul className={styles.validationErrors}>
               {validationState.errors.map((error, index) => (
                 <li key={index} className={styles.validationError}>
                   {error}
                 </li>
               ))}
             </ul>
           </div>
         )} */}
      </Card>
    </div>
  );
};

export default TableField;
